/**
 * A tabela única rota legada do parceiro → rota do App Seazone (hostzone).
 *
 * É a fonte de quais rotas de indicar, de acompanhar indicações e de saldo
 * saem do Sapron, e para onde. Os ids são os mesmos dos dois lados: o App
 * Seazone lê os mesmos endpoints do Sapron (`/partners/indications/location/`
 * e `/partners/v2/indications/investment/`).
 *
 * Cada linha leva a flag do PostHog que a governa (coluna `flag`): indicar e
 * indicações seguem `ff_redirect_hostzone_indicacoes`; Ganhos e Resgatar,
 * `ff_redirect_hostzone_saldo`. Uma flag por superfície, para cada uma ser o
 * kill-switch da sua; juntar as duas é trocar a flag das linhas de saldo.
 *
 * As rotas de saldo são as duas que o `Router.tsx` serve, irmãs sob
 * `/parceiros`: `ganhos` (Ganhos) e `resgate` (Resgatar). `/parceiros/saque` e
 * `/parceiros/ganhos/resgate` não existem como rota (só como constantes do
 * `paths.ts`), então não têm linha.
 *
 * Ficam no Sapron, de propósito, por não terem linha aqui:
 * - `indicar` (índice): segue levando ao painel;
 * - `painel`: o painel do parceiro;
 * - `indicar/formulario/spot-lista` ("Lista Investidores"): sem equivalente;
 * - `indicar/parceiro/convite/:id`: o convite do Fechar;
 * - `indicacoes/spot/previa/contrato`: só existe em dev;
 * - Terreno (`indicar/formulario/terreno`, `indicacoes/terreno`): entra num
 *   PR seguinte, quando o App Seazone abrir a indicação de Terreno.
 */
import { matchPath } from 'react-router-dom';

import { FeatureFlagsOptions } from '@/hooks/useFeatureFlags/types';

/**
 * A base do App Seazone por host do Sapron. Sem variável de ambiente nova no
 * build: o host já diz o ambiente. Host fora do mapa (`localhost`, preview,
 * qualquer outro) não redireciona.
 */
const BASE_POR_HOST: Readonly<Record<string, string>> = {
  'sapron.com.br': 'https://app.seazone.com.br',
  'stg.sapron.com.br': 'https://stg-app.seazone.com.br',
};

/** As abas do quadro do Sapron → o `?produto=` do quadro do App Seazone. */
const PRODUTO_POR_FILTRO: Readonly<Record<string, string>> = {
  locacao: 'locacao',
  construtora: 'construtora',
  spot: 'spot',
  parceiro: 'parceiro',
  decoracao: 'decoracao',
  franquia: 'franquia',
  marketplace: 'revenda',
};

type Parametros = Readonly<Record<string, string | undefined>>;

interface IRotaParaHostzone {
  /** Caminho legado, relativo a `/parceiros`, no formato do React Router. */
  legada: string;
  /** A flag do PostHog que liga esta linha. */
  flag: FeatureFlagsOptions;
  /** Caminho no App Seazone, ou `null` quando a rota fica no Sapron. */
  destino: (parametros: Parametros) => string | null;
}

/**
 * O segmento como veio no endereço. O `matchPath` do React Router 6 não
 * decodifica o parâmetro (`%3F` segue `%3F`); a única troca que ele faz é
 * `%2F` → `/`, desfeita aqui para o id continuar um segmento só. A base é
 * fixa, então o id nunca troca o host do destino.
 */
const id = (parametros: Parametros) => (parametros.id ?? '').replace(/\//g, '%2F');

const fixo = (caminho: string) => (): string => caminho;

const INDICACOES = FeatureFlagsOptions.FF_REDIRECT_HOSTZONE_INDICACOES;
const SALDO = FeatureFlagsOptions.FF_REDIRECT_HOSTZONE_SALDO;

/**
 * Em ordem: a primeira que casa decide. Rota fixa antes da que tem parâmetro
 * no mesmo lugar (`formulario/spot/nova` antes de `formulario/spot/:id`,
 * `spot/previa/contrato` antes de `spot/:id/contrato`), como o próprio React
 * Router ranqueia.
 */
const ROTAS_PARA_HOSTZONE: readonly IRotaParaHostzone[] = [
  { legada: 'indicar/locacao', flag: INDICACOES, destino: fixo('/indicar/locacao') },
  { legada: 'indicar/nova', flag: INDICACOES, destino: fixo('/indicar/locacao') },
  { legada: 'indicar-localizacao', flag: INDICACOES, destino: fixo('/indicar/locacao') },
  { legada: 'indicar/formulario/locacao', flag: INDICACOES, destino: fixo('/indicar/locacao') },
  { legada: 'indicar/nova/:id', flag: INDICACOES, destino: (p) => `/indicar/locacao/${id(p)}` },

  { legada: 'indicar/spot', flag: INDICACOES, destino: fixo('/indicar/spot') },
  { legada: 'indicar/formulario/spot', flag: INDICACOES, destino: fixo('/indicar/spot') },
  { legada: 'indicar/formulario/spot/nova', flag: INDICACOES, destino: fixo('/indicar/spot') },
  { legada: 'indicar/formulario/indicacao-fria', flag: INDICACOES, destino: fixo('/indicar/spot') },
  {
    legada: 'indicar/formulario/spot/:id',
    flag: INDICACOES,
    destino: (p) => `/indicar/spot/${id(p)}/fluxo`,
  },

  {
    legada: 'indicar/formulario/construtora',
    flag: INDICACOES,
    destino: fixo('/indicar/construtora'),
  },
  { legada: 'indicar/formulario/decoracao', flag: INDICACOES, destino: fixo('/indicar/decoracao') },
  { legada: 'indicar/formulario/franquia', flag: INDICACOES, destino: fixo('/indicar/franquia') },
  { legada: 'indicar/formulario/parceiro', flag: INDICACOES, destino: fixo('/indicar/parceiro') },
  { legada: 'indicar/formulario/marketplace', flag: INDICACOES, destino: fixo('/indicar/revenda') },

  { legada: 'indicacoes', flag: INDICACOES, destino: fixo('/indicacoes') },
  { legada: 'indicacoes/spot/previa/contrato', flag: INDICACOES, destino: () => null },
  {
    legada: 'indicacoes/:filter',
    flag: INDICACOES,
    destino: (p) => {
      const produto = PRODUTO_POR_FILTRO[p.filter ?? ''];
      return produto ? `/indicacoes?produto=${produto}` : null;
    },
  },
  {
    legada: 'indicacoes/locacao/:id',
    flag: INDICACOES,
    destino: (p) => `/indicacoes/locacao/${id(p)}`,
  },
  { legada: 'indicacoes/spot/:id', flag: INDICACOES, destino: (p) => `/indicacoes/spot/${id(p)}` },
  {
    legada: 'indicacoes/spot/:id/contrato',
    flag: INDICACOES,
    destino: (p) => `/indicar/spot/${id(p)}/contrato`,
  },

  { legada: 'ganhos', flag: SALDO, destino: fixo('/saques') },
  { legada: 'resgate', flag: SALDO, destino: fixo('/saques/sacar') },
];

/**
 * O endereço completo no App Seazone para a rota legada, ou `null` quando ela
 * fica no Sapron (rota fora da tabela, flag da linha desligada ou host fora do
 * mapa).
 *
 * A rota casa com a linha pelo caminho, como sempre; só depois a flag da linha
 * decide. Desligar uma flag devolve as rotas dela ao Sapron e nunca faz uma
 * delas cair noutra linha.
 */
export function destinoNoHostzone(
  pathname: string,
  hostname: string,
  flagsLigadas: ReadonlySet<FeatureFlagsOptions>,
): string | null {
  const base = BASE_POR_HOST[hostname];
  if (!base) return null;

  const casou = ROTAS_PARA_HOSTZONE.map(({ legada, flag, destino }) => ({
    flag,
    destino,
    casamento: matchPath(`/parceiros/${legada}`, pathname),
  })).find(({ casamento }) => casamento !== null);
  if (!casou?.casamento || !flagsLigadas.has(casou.flag)) return null;

  const caminho = casou.destino(casou.casamento.params);

  return caminho ? `${base}${caminho}` : null;
}
